import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button, Checkbox } from '@jumpstone/ui-react';
import {
  formatRecoveryCode,
  recoveryCodesAsText,
} from '../../lib/auth/two_factor_logic.js';

/**
 * One-time display of freshly minted recovery codes. The server stores only
 * their hashes, so this is the user's only chance to keep them — the
 * Continue button stays disabled until they confirm they have.
 */
export function RecoveryCodesPanel({
  codes,
  onDone,
  doneLabel,
}: {
  codes: readonly string[];
  onDone: () => void;
  doneLabel: string;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  async function copyCodes(): Promise<void> {
    try {
      await navigator.clipboard.writeText(recoveryCodesAsText(codes));
      setCopied(true);
    } catch (err) {
      // Clipboard access can be refused (permissions, insecure context);
      // the codes are still on screen to write down.
      console.error('[wagepro-web] copying recovery codes failed:', err);
      setCopied(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-base font-semibold text-[color:var(--ink)]">
          {t('two_factor.recovery_title')}
        </h2>
        <p className="mt-1 text-sm text-[color:var(--ink-muted)]">
          {t('two_factor.recovery_dek')}
        </p>
      </div>

      <ul className="grid grid-cols-2 gap-2 rounded-lg border border-[color:var(--line)] p-3">
        {codes.map((code) => (
          <li key={code} className="mono text-center text-sm">
            {formatRecoveryCode(code)}
          </li>
        ))}
      </ul>

      <Button
        type="button"
        variant="secondary"
        onClick={() => void copyCodes()}
      >
        {copied
          ? t('two_factor.recovery_copied')
          : t('two_factor.recovery_copy')}
      </Button>

      <label className="flex items-center gap-2 text-sm text-[color:var(--ink)]">
        <Checkbox checked={saved} onCheckedChange={setSaved} />
        {t('two_factor.recovery_confirm')}
      </label>

      <Button
        type="button"
        variant="primary"
        disabled={!saved}
        onClick={onDone}
        className="w-full justify-center"
      >
        {doneLabel}
      </Button>
    </div>
  );
}
